import { useMutation, type UseMutationOptions, useQuery, type UseQueryOptions } from '@tanstack/react-query';

import {
  type GetRouterNetworkQuery,
  type ReadRouterDeviceBody,
  type ReadRouterDeviceResponse,
  type RouterCapacityTestItem,
  type RouterCapacityTestPreflight,
  type RouterNetworkResponse,
  type RouterNetworkStatusItem,
  type SwitchRouterSimResponse,
} from '@okvc/schema';

import { ApiClient } from './api.client.js';
import { type Router } from '../models/index.js';

type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };

export const RoutersApiKeys = {
  Routers: 'Routers',
  NetworkStatus: 'RouterNetworkStatus',
  Network: 'network',
  CapacityTests: 'capacity-tests',
  CapacityTestPreflight: 'capacity-test-preflight',
};

const CAPACITY_TEST_POLL_MS = 3000;
const SIM_SWITCH_POLL_MS = 5000;
const ACTIVE_TEST_STATUSES = new Set(['queued', 'running']);

export function hasActiveCapacityTest(tests: RouterCapacityTestItem[] | undefined): boolean {
  return (tests ?? []).some((test) => ACTIVE_TEST_STATUSES.has(test.status));
}

export class RoutersApi extends ApiClient {
  public static getRouters(): Promise<WithRequired<Router, 'id'>[]> {
    return this.http.get(`/routers`).then((res) => res.data);
  }

  public static getRouter(routerId: string): Promise<Router> {
    return this.http.get(`/routers/${routerId}`).then((res) => res.data);
  }

  public static createRouter(routerDetails: Partial<Router>): Promise<void> {
    return this.http.post('/routers', routerDetails).then((res) => res.data);
  }

  public static updateRouter(routerDetails: Partial<Router & { id: string }>): Promise<void> {
    return this.http.put(`/routers/${routerDetails.id}`, routerDetails).then((res) => res.data);
  }

  public static deleteRouter(routerId: string): Promise<void> {
    return this.http.delete(`/routers/${routerId}`).then((res) => res.data);
  }

  public static getNetworkStatuses(): Promise<RouterNetworkStatusItem[]> {
    return this.http.get('/routers/network-status').then((res) => res.data);
  }

  public static getNetwork(routerId: string, params: GetRouterNetworkQuery): Promise<RouterNetworkResponse> {
    return this.http.get(`/routers/${routerId}/network`, { params }).then((res) => res.data);
  }

  public static getCapacityTests(routerId: string): Promise<RouterCapacityTestItem[]> {
    return this.http.get(`/routers/${routerId}/capacity-tests`).then((res) => res.data);
  }

  public static getCapacityTestPreflight(routerId: string): Promise<RouterCapacityTestPreflight> {
    return this.http.get(`/routers/${routerId}/capacity-tests/preflight`).then((res) => res.data);
  }

  public static startCapacityTest(routerId: string): Promise<RouterCapacityTestItem> {
    return this.http.post(`/routers/${routerId}/capacity-tests`).then((res) => res.data);
  }

  public static cancelCapacityTest(routerId: string, testId: string): Promise<RouterCapacityTestItem> {
    return this.http.delete(`/routers/${routerId}/capacity-tests/${testId}`).then((res) => res.data);
  }

  public static switchSim(routerId: string, simId: string): Promise<SwitchRouterSimResponse> {
    return this.http.post(`/routers/${routerId}/network/switch`, { simId }).then((res) => res.data);
  }

  public static readDevice(body: ReadRouterDeviceBody): Promise<ReadRouterDeviceResponse> {
    return this.http.post('/routers/read-device', body).then((res) => res.data);
  }
}

export function useAllRouters(queryOptions?: UseQueryOptions<WithRequired<Router, 'id'>[]>) {
  return useQuery<WithRequired<Router, 'id'>[]>([RoutersApiKeys.Routers], () => RoutersApi.getRouters(), queryOptions);
}

export function useOneRouter(routerId: string, queryOptions?: UseQueryOptions<Router>) {
  return useQuery<Router>([RoutersApiKeys.Routers, routerId], () => RoutersApi.getRouter(routerId), queryOptions);
}

export function useCreateRouter(mutationOptions?: UseMutationOptions<void, unknown, Partial<Router>>) {
  return useMutation<void, unknown, Partial<Router>>(
    [RoutersApiKeys.Routers],
    (routerDetails: Partial<Router>) => RoutersApi.createRouter(routerDetails),
    mutationOptions,
  );
}

export function useUpdateRouter(mutationOptions?: UseMutationOptions<void, unknown, Partial<Router>>) {
  return useMutation<void, unknown, Partial<Router>>(
    [RoutersApiKeys.Routers],
    (routerDetails: Partial<Router>) => RoutersApi.updateRouter(routerDetails),
    mutationOptions,
  );
}

export function useDeleteRouter(mutationOptions?: UseMutationOptions<void, unknown, string>) {
  return useMutation<void, unknown, string>(
    [RoutersApiKeys.Routers],
    (routerId: string) => RoutersApi.deleteRouter(routerId),
    mutationOptions,
  );
}

export function useRouterNetworkStatuses(queryOptions?: UseQueryOptions<RouterNetworkStatusItem[]>) {
  return useQuery<RouterNetworkStatusItem[]>(
    [RoutersApiKeys.NetworkStatus],
    () => RoutersApi.getNetworkStatuses(),
    queryOptions,
  );
}

export function useRouterNetwork(routerId: string, params: GetRouterNetworkQuery) {
  return useQuery<RouterNetworkResponse>(
    [RoutersApiKeys.Routers, routerId, RoutersApiKeys.Network, params.days, params.timezone],
    () => RoutersApi.getNetwork(routerId, params),
    {
      keepPreviousData: true,
      refetchInterval: (network) => (network?.router.networkState === 'switching' ? SIM_SWITCH_POLL_MS : false),
    },
  );
}

export function useRouterCapacityTests(routerId: string) {
  return useQuery<RouterCapacityTestItem[]>(
    [RoutersApiKeys.Routers, routerId, RoutersApiKeys.CapacityTests],
    () => RoutersApi.getCapacityTests(routerId),
    { refetchInterval: (tests) => (hasActiveCapacityTest(tests) ? CAPACITY_TEST_POLL_MS : false) },
  );
}

export function useCapacityTestPreflight(routerId: string, enabled: boolean) {
  return useQuery<RouterCapacityTestPreflight>(
    [RoutersApiKeys.Routers, routerId, RoutersApiKeys.CapacityTestPreflight],
    () => RoutersApi.getCapacityTestPreflight(routerId),
    { enabled, cacheTime: 0 },
  );
}

export function useStartCapacityTest(routerId: string) {
  return useMutation<RouterCapacityTestItem, unknown, void>(() => RoutersApi.startCapacityTest(routerId));
}

export function useSwitchRouterSim(routerId: string) {
  return useMutation<SwitchRouterSimResponse, unknown, string>((simId: string) => RoutersApi.switchSim(routerId, simId));
}

export function useReadRouterDevice() {
  return useMutation<ReadRouterDeviceResponse, unknown, ReadRouterDeviceBody>(
    (body: ReadRouterDeviceBody) => RoutersApi.readDevice(body),
  );
}

export function useCancelCapacityTest(routerId: string) {
  return useMutation<RouterCapacityTestItem, unknown, string>(
    (testId: string) => RoutersApi.cancelCapacityTest(routerId, testId),
  );
}
